import { ActionReducerMapBuilder, PayloadAction, createSlice } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import {
  AssignDialogStateType,
  AssignWaveType,
  TaskDetailDialogStateType,
  TaskWaveType,
  ReplaceStaffDialogStateType,
  CompletePoolingDialogStateType,
} from 'features/SupplyOrder/pages/XdockV2/@types'
import { SOPoolDetailInterface, XDockTaskInterface } from 'features/SupplyOrder/@types/XDock'
import {
  SLICE_NAME,
  fetchXdockTaskList,
  fetchDtcPoolDetailList,
  fetchCrewListByWave,
  submitWaveAssignment,
  fetchTaskDetail,
  fetchReplaceStaffList,
  submitReplaceStaff,
  fetchWarehouseOptions,
  fetchPoolingDetail,
  submitCompletePooling,
} from './thunk'

interface XdockStateType {
  taskList: XDockTaskInterface[]
  dtcTaskList: SOPoolDetailInterface[]
  isLoading: boolean
  totalElements: number
  dtcTotalElements: number
  assignDialog: AssignDialogStateType
  taskDetailDialog: TaskDetailDialogStateType
  replaceStaffDialog: ReplaceStaffDialogStateType
  completePoolingDialog: CompletePoolingDialogStateType
}

const initialAssignDialog: AssignDialogStateType = {
  open: false,
  selectedItemId: null,
  locationId: null,
  currentStep: 0,
  waves: [],
  waveSelections: [],
  staffList: [],
  searchQuery: '',
}

const initialTaskDetailDialog: TaskDetailDialogStateType = {
  open: false,
  itemId: null,
  locationId: null,
  productName: '',
  productImage: '',
  sku_number: '',
  waves: [],
}

const initialReplaceStaffDialog: ReplaceStaffDialogStateType = {
  open: false,
  itemId: null,
  locationId: null,
  waveNumber: null,
  currentStaff: null,
  staffList: [],
  selectedStaffId: null,
  searchQuery: '',
}

const initialCompletePoolingDialog: CompletePoolingDialogStateType = {
  open: false,
  currentStep: 0,
  warehouseOptions: [],
  selectedWarehouse: null,
  poolingDetail: null,
  nextProcess: null,
}

const initialState: XdockStateType = {
  taskList: [],
  dtcTaskList: [],
  isLoading: false,
  totalElements: 0,
  dtcTotalElements: 0,
  assignDialog: initialAssignDialog,
  taskDetailDialog: initialTaskDetailDialog,
  replaceStaffDialog: initialReplaceStaffDialog,
  completePoolingDialog: initialCompletePoolingDialog,
}

const xdockSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetXdockState: () => initialState,
    openAssignDialog: (
      state,
      action: PayloadAction<{
        itemId: number
        locationId: number | null
        waves: AssignWaveType[]
      }>,
    ) => {
      state.assignDialog.open = true
      state.assignDialog.selectedItemId = action.payload.itemId
      state.assignDialog.locationId = action.payload.locationId
      state.assignDialog.currentStep = 0
      state.assignDialog.waves = action.payload.waves
      state.assignDialog.waveSelections = action.payload.waves.map(() => null)
      state.assignDialog.searchQuery = ''
    },
    closeAssignDialog: (state) => {
      state.assignDialog = initialAssignDialog
    },
    setAssignDialogStep: (state, action: PayloadAction<number>) => {
      state.assignDialog.currentStep = action.payload
      state.assignDialog.searchQuery = ''
    },
    setAssignDialogSearch: (state, action: PayloadAction<string>) => {
      state.assignDialog.searchQuery = action.payload
    },
    setAssignDialogCrewSelection: (
      state,
      action: PayloadAction<{ step: number; crewId: number | null }>,
    ) => {
      state.assignDialog.waveSelections[action.payload.step] = action.payload.crewId
    },
    openTaskDetailDialog: (
      state,
      action: PayloadAction<{ itemId: number; locationId: number | null }>,
    ) => {
      state.taskDetailDialog.open = true
      state.taskDetailDialog.itemId = action.payload.itemId
      state.taskDetailDialog.locationId = action.payload.locationId
    },
    closeTaskDetailDialog: (state) => {
      state.taskDetailDialog = initialTaskDetailDialog
    },
    openReplaceStaffDialog: (
      state,
      action: PayloadAction<{ waveNumber: number; currentStaff: TaskWaveType['staff'] | null }>,
    ) => {
      state.replaceStaffDialog = {
        ...initialReplaceStaffDialog,
        open: true,
        itemId: state.taskDetailDialog.itemId,
        locationId: state.taskDetailDialog.locationId,
        waveNumber: action.payload.waveNumber,
        currentStaff: action.payload.currentStaff,
      }
    },
    closeReplaceStaffDialog: (state) => {
      state.replaceStaffDialog = initialReplaceStaffDialog
    },
    setReplaceStaffSearch: (state, action: PayloadAction<string>) => {
      state.replaceStaffDialog.searchQuery = action.payload
    },
    setReplaceStaffSelection: (state, action: PayloadAction<number>) => {
      state.replaceStaffDialog.selectedStaffId = action.payload
    },
    openCompletePoolingDialog: (state) => {
      state.completePoolingDialog.open = true
      state.completePoolingDialog.currentStep = 0
    },
    closeCompletePoolingDialog: (state) => {
      state.completePoolingDialog = initialCompletePoolingDialog
    },
    setCompletePoolingStep: (state, action: PayloadAction<0 | 1 | 2>) => {
      state.completePoolingDialog.currentStep = action.payload
    },
    setCompletePoolingWarehouse: (state, action: PayloadAction<{ id: number; name: string }>) => {
      state.completePoolingDialog.selectedWarehouse = action.payload
      state.completePoolingDialog.poolingDetail = null // reset on warehouse change
    },
    setCompletePoolingNextProcess: (
      state,
      action: PayloadAction<CompletePoolingDialogStateType['nextProcess']>,
    ) => {
      state.completePoolingDialog.nextProcess = action.payload
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<XdockStateType>) => {
      builder.addCase(fetchXdockTaskList.fulfilled, (state, { payload }) => {
        state.taskList = payload.data
        state.totalElements = payload.total
      })
      builder.addCase(fetchDtcPoolDetailList.fulfilled, (state, { payload }) => {
        state.dtcTaskList = payload.data
        state.dtcTotalElements = payload.total
      })
      builder.addCase(fetchCrewListByWave.fulfilled, (state, { payload }) => {
        state.assignDialog.staffList = payload
      })
      builder.addCase(submitWaveAssignment.fulfilled, (state) => {
        // Reset dialog after successful submit — sorting refetch handled by useAssignDialog
        state.assignDialog = initialAssignDialog
      })
      builder.addCase(fetchTaskDetail.fulfilled, (state, { payload }) => {
        state.taskDetailDialog.productName = payload.productName
        state.taskDetailDialog.productImage = payload.productImage
        state.taskDetailDialog.sku_number = payload.sku_number
        state.taskDetailDialog.waves = payload.waves
      })
      builder.addCase(fetchReplaceStaffList.fulfilled, (state, { payload }) => {
        state.replaceStaffDialog.staffList = payload
      })
      builder.addCase(submitReplaceStaff.fulfilled, (state) => {
        // Reset dialog after successful submit — detail refetch handled by useReplaceStaffDialog
        state.replaceStaffDialog = initialReplaceStaffDialog
      })
      builder.addCase(fetchWarehouseOptions.fulfilled, (state, { payload }) => {
        state.completePoolingDialog.warehouseOptions = payload
      })
      builder.addCase(fetchPoolingDetail.fulfilled, (state, { payload }) => {
        state.completePoolingDialog.poolingDetail = payload
      })
      builder.addCase(submitCompletePooling.fulfilled, (state) => {
        state.completePoolingDialog = initialCompletePoolingDialog
      })
    },
    [
      fetchXdockTaskList,
      fetchDtcPoolDetailList,
      fetchCrewListByWave,
      submitWaveAssignment,
      fetchTaskDetail,
      fetchReplaceStaffList,
      submitReplaceStaff,
      fetchWarehouseOptions,
      fetchPoolingDetail,
      submitCompletePooling,
    ],
  ),
})

export const {
  resetXdockState,
  openAssignDialog,
  closeAssignDialog,
  setAssignDialogStep,
  setAssignDialogSearch,
  setAssignDialogCrewSelection,
  openTaskDetailDialog,
  closeTaskDetailDialog,
  openReplaceStaffDialog,
  closeReplaceStaffDialog,
  setReplaceStaffSearch,
  setReplaceStaffSelection,
  openCompletePoolingDialog,
  closeCompletePoolingDialog,
  setCompletePoolingStep,
  setCompletePoolingWarehouse,
  setCompletePoolingNextProcess,
} = xdockSlice.actions
export default xdockSlice.reducer
